import { Component, OnInit, EventEmitter, Output, Inject, ViewChild } from '@angular/core';
import { State, process, filterBy, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridDataResult, RowClassArgs } from '@progress/kendo-angular-grid';
import { Observable } from 'rxjs';
import { IPerformanceBodPackages } from 'src/app/api/performance-bod-package';
import { IPerformanceBodPads } from 'src/app/api/performance-bod-pads';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { DragAndDropService } from 'src/app/_shared/drag-and-drop.service';
import { PermissionsProvider } from 'src/app/services/permissions.provider';
import { PerformanceBodStoreService } from 'src/app/services/performance-bod-store.service';
import { map } from 'rxjs';
import { InfrastructureRoadMaintenanceService } from './infrastructure-road-maintenance.service';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { AuthenticationService } from 'src/app/services';
import { IPerformanceBodRoadMaintenance } from 'src/app/api/performance-bod-road-maintenance';
import { Guid } from 'guid-typescript';
import { UtilService } from 'src/app/services/util.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationService } from '@progress/kendo-angular-notification';

const DRAGGABLE_SELECTOR = '.infrastructure-road-maintenance-draggable';

@Component({
    selector: 'app-infrastructure-road-maintenance',
    templateUrl: './infrastructure-road-maintenance.component.html',
    standalone: false
})
export class InfrastructureRoadMaintenanceComponent implements OnInit {
  @Output() public performanceBodUpdate = new EventEmitter<string>();
  @Output() public saveBodCompleted = new EventEmitter<boolean>();
  @ViewChild('infraRoadMaintenance') public infraRoadMaintenanceGrid: any;
  public warningMessage: string =
    'Please provide a Well Pad Name on the Package Information Section (Performance BOD Summary Tab) before adding a new record';
  public openDialog: boolean = false;
  public filter: CompositeFilterDescriptor;
  public filterable: boolean = true;
  public countWellPadName: number = 0;
  public messageDuplicate: boolean = true;
  public view: Observable<GridDataResult>;
  public performanceBodPackagesList: IPerformanceBodPackages[];
  public performanceBodPadsList: IPerformanceBodPads[];
  public performanceBodId: string;
  public formGroup: UntypedFormGroup;
  public gridState: State = {
    sort: [],
    skip: 0,
  };
  public canEditPerformanceBod: boolean = false;
  private dragAndDropService: DragAndDropService;
  public infrastructureRoadMaintenanceService: InfrastructureRoadMaintenanceService;

  constructor(
    private performanceBodStoreService: PerformanceBodStoreService,
    private permissionsProvider: PermissionsProvider,
    private authenticationService: AuthenticationService,
    private formBuilder: UntypedFormBuilder,
    @Inject(DragAndDropService) dragAndDropServiceFactory: any,
    @Inject(InfrastructureRoadMaintenanceService) infrastructureRoadMaintenanceServiceFactory: any,
    private route: ActivatedRoute,
    private utilService: UtilService,
    private spinner: NgxSpinnerService,
    private notificationService: NotificationService
  ) {
    this.dragAndDropService = dragAndDropServiceFactory();
    this.infrastructureRoadMaintenanceService = infrastructureRoadMaintenanceServiceFactory();
  }
  ngOnInit(): void {
    this.performanceBodStoreService.performanceBodSignatured$.subscribe((isSignature) => {
      this.canEditPerformanceBod = isSignature? false: this.permissionsProvider.canEditPerformanceBod;
    });
    this.performanceBodStoreService.performanceBodPadsData$.subscribe(data => {
      this.performanceBodPadsList = data.filter(row => row.wellPadName !== '');
    });
    this.performanceBodStoreService.performanceBodPackagesData$.subscribe(data => {
      this.performanceBodPackagesList = data;
    });

    this.view = this.infrastructureRoadMaintenanceService.pipe(
      map(data => {
        this.dragAndDropService.dataSourceRefreshed(data);
        return process(filterBy(data, this.filter), this.gridState);
      })
    );

    this.route.paramMap.subscribe((params: ParamMap) => {
      this.infrastructureRoadMaintenanceService.performanceBodId = params.get('id');
      this.infrastructureRoadMaintenanceService.read();
    });

    this.dragAndDropService.initialize(this.infrastructureRoadMaintenanceService.data, DRAGGABLE_SELECTOR, () => {
      this.infrastructureRoadMaintenanceService.markAsSetOrder(this.authenticationService.getCurrentUser());
    });
  }

  public ngOnDestroy(): void {
    this.dragAndDropService.refreshDragAndDrop();
  }

  public createFormGroup(dataItem: any): UntypedFormGroup {
    return this.formBuilder.group({
      id: new UntypedFormControl(dataItem.id),
      isDeleted: new UntypedFormControl(dataItem.isDeleted, [Validators.required]),
      chargeCode: new UntypedFormControl(dataItem.chargeCode, [
        Validators.maxLength(13),
        Validators.pattern('^[a-zA-Z0-9]{0,13}$'),
      ]),
      cndHOEstimate: new UntypedFormControl(dataItem.cndHOEstimate, [Validators.min(0), Validators.max(999999999999)]),
      comments: new UntypedFormControl(dataItem.comments, [Validators.maxLength(500)]),
      performanceBodPackagesId: new UntypedFormControl(dataItem.performanceBodPackagesId),
      performanceBodPadsId: new UntypedFormControl(dataItem.performanceBodPadsId, [Validators.required]),
      createdBy: new UntypedFormControl(dataItem.createdBy),
      updatedBy: new UntypedFormControl(dataItem.updatedBy),
      afeNumber: new UntypedFormControl(dataItem.afeNumber, [
        Validators.maxLength(13),
        Validators.pattern('^[a-zA-Z0-9]{0,13}$'),
      ]),  
      afeAmount: new UntypedFormControl(dataItem.afeAmount, [
        Validators.maxLength(8),
        Validators.pattern('^[a-zA-Z0-9]{0,8}$'),
      ]), 
    });
  }

  public cellClickHandler({ sender, rowIndex, columnIndex, dataItem, isEdited }) {
    if (!isEdited && this.canEditPerformanceBod) {
      sender.editCell(rowIndex, columnIndex, this.createFormGroup(dataItem));
      this.dragAndDropService.onEditingMode();
    }
  }

  public cellCloseHandler(args: any) {
    const { formGroup, dataItem, column } = args;

    let columnField = '';

    if (column.field === 'performanceBodPackagesName') {
      columnField = 'performanceBodPackagesId';
    } else {
      columnField = column.field;
    }

    const checkColumn = formGroup.controls[columnField];

    if (!checkColumn.valid) {
      args.preventDefault();
    } else if (checkColumn.dirty) {
      if (columnField === 'performanceBodPackagesId') {
        dataItem.performanceBodPackagesName = this.getPerformanceBodPackages(formGroup.controls[columnField].value);
      }
    }

    if (!formGroup.valid) {
      // prevent closing the edited cell if there are invalid values.
      args.preventDefault();
    } else if (formGroup.dirty) {
      if (formGroup.valid && args.sender.data.total > 0) {
        for (let i = 0; i <= args.sender.data.total - 1; i++) {
          if (
            args.sender.data.data[i].performanceBodPadsId === formGroup.value.performanceBodPadsId &&
            args.sender.data.data[i].id !== formGroup.value.id
          ) {
            this.messageDuplicate = false;
            args.preventDefault();
            return;
          } else {
            this.messageDuplicate = true;
          }
        }
      }

      if (this.messageDuplicate) {
        dataItem.updatedDate = new Date();
        formGroup.value.updatedBy = this.authenticationService.getCurrentUser();
        formGroup.value.updatedDate = dataItem.updatedDate;

        this.infrastructureRoadMaintenanceService.assignValues(dataItem, formGroup.value);
        this.infrastructureRoadMaintenanceService.updateItem(dataItem);
      }
    }
    // calls this to add the attribute of current row
    this.dragAndDropService.onEditingClose();
    this.dragAndDropService.refreshDragAndDrop();
  }

  public cancelHandler({ sender, rowIndex }) {
    this.messageDuplicate = true;
    sender.closeRow(rowIndex);
    this.dragAndDropService.onEditingClose();
    this.dragAndDropService.refreshDragAndDrop();
  }

  public addHandler({ sender, rowIndex }) {
    const item: IPerformanceBodRoadMaintenance = {
      id: Guid.create().toString(),
      isDeleted: false,
      chargeCode: '',
      cndHOEstimate: 0,
      comments: null,
      performanceBodPadsId: null,
      performanceBodPads: null,
      createdBy: this.authenticationService.getCurrentUser(),
      createdDate: new Date(),
      updatedBy: this.authenticationService.getCurrentUser(),
      updatedDate: new Date(),
      afeNumber:'',
      afeAmount: 0,
    };

    if (this.performanceBodPadsList) {
      for (const pad of this.performanceBodPadsList) {
        if (pad.wellPadName === '') {
          this.countWellPadName = this.countWellPadName + 1;
        }
      }
      if (this.countWellPadName === this.performanceBodPadsList.length) {
        this.openDialog = true;
        this.cancelHandler({ sender, rowIndex });
        this.countWellPadName = 0;
        return;
      } else {
        item.createdBy = this.authenticationService.getCurrentUser();
        item.createdDate = new Date();
        item.updatedBy = this.authenticationService.getCurrentUser();
        item.updatedDate = new Date();
        sender.addRow(this.createFormGroup(item));
        this.dragAndDropService.onEditingMode();
      }
    }
  }

  public saveHandler({ sender, formGroup, rowIndex }) {
    if (formGroup.valid && sender.data.total > 0) {
      for (let x = 0; x <= sender.data.total - 1; x++) {
        if (sender.data.data[x].performanceBodPadsId === formGroup.value.performanceBodPadsId) {
          this.messageDuplicate = false;
          return;
        } else {
          this.messageDuplicate = true;
        }
      }
    }
    if (this.messageDuplicate) {
      if (formGroup.valid) {
        formGroup.patchValue({
          performanceBodId: this.performanceBodId,
        });

        if (this.performanceBodPadsList) {
          for (const pad of this.performanceBodPadsList) {
            if (pad.wellPadName === '') {
              this.countWellPadName = this.countWellPadName + 1;
            }
          }
          if (this.countWellPadName === this.performanceBodPadsList.length) {
            this.openDialog = true;
            this.countWellPadName = 0;
            return;
          } else {
            this.infrastructureRoadMaintenanceService.createItem(formGroup.value);
            sender.closeRow(rowIndex);
            this.dragAndDropService.onEditingClose();
            this.dragAndDropService.dataSourceRefreshed(this.infrastructureRoadMaintenanceService.data);
            this.dragAndDropService.refreshDragAndDrop();
            this.infrastructureRoadMaintenanceService.markAsSetOrder(this.permissionsProvider.cai);
          }
        }
      } else {
        this.utilService.markFormGroupAsTouched(formGroup);
      }
    }
  }

  public removeHandler({ sender, dataItem }) {
    dataItem.isDeleted = true;
    dataItem.updatedDate = new Date();
    dataItem.updatedBy = this.authenticationService.getCurrentUser();
    this.infrastructureRoadMaintenanceService.removeItem(dataItem);

    sender.cancelCell();
  }

  public saveChanges(grid: any): void {
    grid.closeCell();
    grid.cancelCell();
    this.spinner.show();
    // calls this to add the attribute of current row
    this.dragAndDropService.refreshDragAndDrop();
    this.infrastructureRoadMaintenanceService.saveChanges();
    this.performanceBodUpdate.next(this.authenticationService.getCurrentUser());

    setTimeout(() => {
      this.spinner.hide();
      this.notificationService.show({
        content: 'Infrastructure - Road Maintenance saved',
        position: { horizontal: 'center', vertical: 'top' },
        animation: { type: 'fade', duration: 500 },
        type: { style: 'success', icon: true },
      });
      this.infrastructureRoadMaintenanceService.cancelChanges();
    }, 5000);
  }

  public saveFromBOD(): void {
    this.infraRoadMaintenanceGrid.closeCell();
    this.infraRoadMaintenanceGrid.cancelCell();

    if (this.infrastructureRoadMaintenanceService.hasChanges()) {
      this.spinner.show();
      // calls this to add the attribute of current row
      this.dragAndDropService.refreshDragAndDrop();
      this.infrastructureRoadMaintenanceService.saveChanges();
      this.performanceBodUpdate.next(this.authenticationService.getCurrentUser());

      const subscription = this.infrastructureRoadMaintenanceService.subscribe(() => {
        this.saveBodCompleted.next(true);
      });

      subscription.unsubscribe();
    }
  }

  public cancelChanges(grid: any): void {
    this.messageDuplicate = true;
    this.infraRoadMaintenanceGrid.cancelCell();
    this.dragAndDropService.refreshDragAndDrop();
    this.infrastructureRoadMaintenanceService.cancelChanges();
  }

  public onStateChange(state: State) {
    this.gridState = state;
    this.infrastructureRoadMaintenanceService.read();
  }

  public getPerformanceBodPackages(id: string): any {
    if (this.performanceBodPackagesList === null || this.performanceBodPackagesList === undefined) {
      return '';
    }
    this.performanceBodPackagesList = this.performanceBodPackagesList.filter(x => x.id !== null && x.id !== undefined);
    return this.performanceBodPackagesList.find(x => x.id === id);
  }

  public changePackage(value, dataItem, formGroup: UntypedFormGroup) {
    let performanceBodPad: IPerformanceBodPads[];
    let performanceBodPackagesId: string;
    performanceBodPad = this.performanceBodPadsList.filter(s => s.id === value);
    performanceBodPad.forEach(element => {
      performanceBodPackagesId = element.performanceBodPackagesId;
    });

    dataItem.performanceBodPackagesId = performanceBodPackagesId;
    formGroup.patchValue({
      performanceBodPackagesId,
    });
  }

  public getPerformanceBodPads(id: string): any {
    if (this.performanceBodPadsList === null || this.performanceBodPadsList === undefined) {
      return '';
    } else {
      return this.performanceBodPadsList.find(x => x.id === id);
    }
  }

  public closeKendoPopUp() {
    this.openDialog = false;
  }

  public handlerFilter(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    if (filter.filters) {
      if (filter.filters.length > 0) {
        this.dragAndDropService.onEditingMode();
      } else {
        this.dragAndDropService.onEditingClose();
        this.dragAndDropService.refreshDragAndDrop();
      }
    }
  }

  public rowCallback(context: RowClassArgs) {
    return {
      dragging: context.dataItem.dragging,
    };
  }

  public rowLoaded(): void {
    // to check until grid have been loeaded.
    this.dragAndDropService.checkIsOnEditMode();
    this.dragAndDropService.checkDraggableElementLoaded();
  }
}
